// Color-theory based palette variation in LCH space.
//
// The app stores every palette entry as CIELAB (canonical). LCH is the polar
// form of the same space: L = lightness, C = chroma, h = hue angle (degrees).
// Working in LCH lets us rotate hue while keeping lightness/chroma, which is
// what "complementary / triadic / analogous" variation means perceptually.
//
// Gamut rule (project requirement): never clip channels. If a color falls
// outside sRGB we reduce C (binary search) until it fits, keeping L and h.
//
// References: Itten's color-theory constructions (complementary, triadic,
// analogous, split-complementary) expressed in CIELAB/LCH; see also
// Chang et al. 2015 (palette-based recoloring) for editing in a fixed space.
//
// All functions here are PURE and DETERMINISTIC: the same input (including the
// optional seed) always produces the same palette, so swatches can be
// regenerated without surprises and tested with deep equality.

import type { LabColor, PaletteColor } from '../types';

export type ColorTheory =
  | 'complementary'
  | 'triadic'
  | 'analogous'
  | 'monochromatic'
  | 'split-complementary';

export const COLOR_THEORIES: { id: ColorTheory; label: string }[] = [
  { id: 'complementary', label: 'Complementary' },
  { id: 'triadic', label: 'Triadic' },
  { id: 'analogous', label: 'Analogous' },
  { id: 'monochromatic', label: 'Monochromatic' },
  { id: 'split-complementary', label: 'Split-Comp.' },
];

export interface LCH {
  L: number; // 0..100
  C: number; // 0..~150
  h: number; // degrees, 0..360
}

const DEG = Math.PI / 180;

export function normalizeHue(h: number): number {
  const wrapped = h % 360;
  return wrapped < 0 ? wrapped + 360 : wrapped;
}

/** Signed shortest angular difference a -> b, in (-180, 180]. */
export function shortestHueDelta(from: number, to: number): number {
  return ((to - from + 540) % 360) - 180;
}

/** Unsigned angular distance (0..180). */
export function hueDistance(a: number, b: number): number {
  return Math.abs(shortestHueDelta(a, b));
}

export function labToLch(lab: LabColor): LCH {
  const C = Math.sqrt(lab.a * lab.a + lab.b * lab.b);
  return { L: lab.L, C, h: normalizeHue(Math.atan2(lab.b, lab.a) / DEG) };
}

export function lchToLab(lch: LCH): LabColor {
  return {
    L: lch.L,
    a: lch.C * Math.cos(lch.h * DEG),
    b: lch.C * Math.sin(lch.h * DEG),
  };
}

/* ------------------------------------------------------------------ */
/* sRGB gamut membership (unclamped)                                   */
/* ------------------------------------------------------------------ */

/**
 * Lab -> linear sRGB WITHOUT clamping. The public core helper
 * (labToRgbLinear) clamps to [0,1], which hides gamut violations — for gamut
 * mapping we need the raw values.
 */
function labToLinearUnclamped(lab: LabColor): { r: number; g: number; b: number } {
  const fy = (lab.L + 16) / 116;
  const fx = lab.a / 500 + fy;
  const fz = fy - lab.b / 200;

  const delta = 6 / 29;
  const xr = fx > delta ? fx * fx * fx : 3 * delta * delta * (fx - 4 / 29);
  const yr = lab.L > 8 ? fy * fy * fy : 3 * delta * delta * (fy - 4 / 29);
  const zr = fz > delta ? fz * fz * fz : 3 * delta * delta * (fz - 4 / 29);

  // D65 white
  const X = xr * 0.95047;
  const Y = yr * 1.0;
  const Z = zr * 1.08883;

  return {
    r: X * 3.2404542 + Y * -1.5371385 + Z * -0.4985314,
    g: X * -0.969266 + Y * 1.8760108 + Z * 0.041556,
    b: X * 0.0556434 + Y * -0.2040259 + Z * 1.0572252,
  };
}

const GAMUT_EPS = 1e-4;

export function isLchInSrgbGamut(lch: LCH): boolean {
  if (lch.C < 0 || lch.L < 0 || lch.L > 100) return false;
  const rgb = labToLinearUnclamped(lchToLab(lch));
  return (
    rgb.r >= -GAMUT_EPS &&
    rgb.r <= 1 + GAMUT_EPS &&
    rgb.g >= -GAMUT_EPS &&
    rgb.g <= 1 + GAMUT_EPS &&
    rgb.b >= -GAMUT_EPS &&
    rgb.b <= 1 + GAMUT_EPS
  );
}

/**
 * Bring an LCH color into sRGB by REDUCING chroma only (binary search).
 * L and h are preserved exactly, so hue relationships between layers survive.
 * Colors that are already in gamut are returned unchanged (identity).
 */
export function gamutMapLch(lch: LCH): LCH {
  const L = Math.max(0, Math.min(100, lch.L));
  const h = normalizeHue(lch.h);
  const C = Math.max(0, lch.C);
  const start: LCH = { L, C, h };
  if (isLchInSrgbGamut(start)) return start;

  let lo = 0;
  let hi = C;
  for (let i = 0; i < 32; i++) {
    const mid = (lo + hi) / 2;
    if (isLchInSrgbGamut({ L, C: mid, h })) lo = mid;
    else hi = mid;
  }
  return { L, C: lo, h };
}

export function gamutMapLchToLab(lch: LCH): LabColor {
  return lchToLab(gamutMapLch(lch));
}

/* ------------------------------------------------------------------ */
/* Pure per-theory hue functions                                       */
/* ------------------------------------------------------------------ */

/**
 * Complementary: layers whose hue is within 90 deg of the anchor keep their
 * hue (anchor family); the others move to the opposite side by +180 deg,
 * keeping their offset relative to the anti-anchor.
 */
export function complementaryHue(h: number, anchorHue: number): number {
  return Math.abs(shortestHueDelta(anchorHue, h)) <= 90 ? h : normalizeHue(h + 180);
}

/** Assign to the nearest of {h, h+120, h+240}. */
export function triadicHue(h: number, anchorHue: number): number {
  return nearestTargetHue(h, anchorHue, [0, 120, 240]);
}

/** Assign to the nearest of {h, h+150, h+210}. */
export function splitComplementaryHue(h: number, anchorHue: number): number {
  return nearestTargetHue(h, anchorHue, [0, 150, 210]);
}

/**
 * Analogous: rotate every layer by the SAME shift, so hue relationships are
 * preserved. Depth of the shift is chosen by the variant (see shift table).
 */
export function analogousHue(h: number, shift: number): number {
  return normalizeHue(h + shift);
}

/** Monochromatic: every layer sits on the anchor hue. */
export function monochromaticHue(anchorHue: number): number {
  return normalizeHue(anchorHue);
}

function nearestTargetHue(h: number, anchorHue: number, offsets: number[]): number {
  let best = normalizeHue(anchorHue + offsets[0]);
  let bestD = hueDistance(h, best);
  for (let i = 1; i < offsets.length; i++) {
    const candidate = normalizeHue(anchorHue + offsets[i]);
    const d = hueDistance(h, candidate);
    if (d < bestD) {
      best = candidate;
      bestD = d;
    }
  }
  return best;
}

/* ------------------------------------------------------------------ */
/* Variant generation                                                  */
/* ------------------------------------------------------------------ */

export type PaperMode = 'keep' | 'include';

export interface GenerateVariantsOptions {
  theory: ColorTheory;
  colors: PaletteColor[];      // layer order (index 0 = paper by convention)
  anchorIndex: number;
  paperMode: PaperMode;
  locked: boolean[];           // per layer, true = keep exact original
  preserveLightness: boolean;
  count: number;               // 4..8
  /** Shuffle seed. 0 = the canonical (deterministic) sequence. */
  seed?: number;
}

/**
 * Deterministic variant tables. Variant 0 is always the identity; the seed
 * (Shuffle) only shifts the table offset, so there is never any randomness
 * involved and 8 consecutive variants are always distinct.
 */
const ROTATIONS = [0, 15, -15, 30, -30, 45, -45, 20];
const ANALOGOUS_SHIFTS = [0, 15, -15, 30, -30, 45, -45, 20];
const L_DELTAS = [0, 4, -4, 8, -8, 6, -6, 10];
const MONO_L_DELTAS = [0, 3, -3, 5, -5, 4, -4, 6];
const C_FACTORS = [1, 1.15, 0.85, 1.25, 0.75, 1.1, 0.9, 1.2];

/** table[0] for v===0, otherwise cycle through table[1..] shifted by the seed. */
function pickVariant<T>(table: T[], v: number, seed: number): T {
  if (v === 0) return table[0];
  return table[1 + ((v - 1 + seed) % (table.length - 1))];
}

interface VariantParams {
  /** Hue rotation applied to the whole palette (degrees). */
  rotation: number;
  /** Uniform lightness delta (keeps the L ordering intact). */
  lDelta: number;
  /** Chroma multiplier. */
  cFactor: number;
}

function variantParams(
  theory: ColorTheory,
  v: number,
  seed: number,
  preserveLightness: boolean
): VariantParams {
  if (v === 0) return { rotation: 0, lDelta: 0, cFactor: 1 };

  if (theory === 'monochromatic') {
    // Lightness MUST change for monochromatic variants; a uniform delta keeps
    // the ordering between layers (assumption documented in NOTES.md).
    const scale = preserveLightness ? 1 : 2;
    return {
      rotation: 0,
      lDelta: pickVariant(MONO_L_DELTAS, v, seed) * scale,
      cFactor: pickVariant(C_FACTORS, v, seed),
    };
  }

  if (theory === 'analogous') {
    return {
      rotation: pickVariant(ANALOGOUS_SHIFTS, v, seed),
      lDelta: preserveLightness ? 0 : pickVariant(L_DELTAS, v, seed),
      cFactor: 1,
    };
  }

  // complementary / triadic / split-complementary: rotate the whole scheme so
  // the two/three hue families stay intact but each swatch differs.
  return {
    rotation: pickVariant(ROTATIONS, v, seed),
    lDelta: preserveLightness ? 0 : pickVariant(L_DELTAS, v, seed),
    cFactor: 1,
  };
}

/**
 * Generate `count` deterministic palette variants.
 * Returns Lab colors in the same layer order as the input.
 *
 * Rules applied per layer:
 *  - locked layers are copied verbatim;
 *  - the paper layer is untouched when paperMode === 'keep';
 *  - hue follows the selected theory (pure functions above);
 *  - L is preserved unless preserveLightness is false (uniform delta, so the
 *    ordering between layers never changes);
 *  - monochromatic always varies L uniformly and scales C;
 *  - every result is gamut-mapped by reducing C only.
 */
export function generateVariants(opts: GenerateVariantsOptions): LabColor[][] {
  const {
    theory,
    colors,
    anchorIndex,
    paperMode,
    locked,
    preserveLightness,
    count,
    seed = 0,
  } = opts;

  if (colors.length === 0 || count <= 0) return [];

  const lchs = colors.map((c) => labToLch(c.lab));
  const anchorIdx = Math.max(0, Math.min(colors.length - 1, anchorIndex));
  const anchorHue = lchs[anchorIdx].h;

  const out: LabColor[][] = [];
  for (let v = 0; v < count; v++) {
    const p = variantParams(theory, v, seed, preserveLightness);
    const variant = lchs.map((lch, i) => {
      if (locked[i]) return { ...colors[i].lab };
      if (colors[i].isPaper && paperMode === 'keep') return { ...colors[i].lab };

      let h: number;
      switch (theory) {
        case 'complementary':
          h = complementaryHue(lch.h, anchorHue);
          break;
        case 'triadic':
          h = triadicHue(lch.h, anchorHue);
          break;
        case 'split-complementary':
          h = splitComplementaryHue(lch.h, anchorHue);
          break;
        case 'analogous':
          h = analogousHue(lch.h, p.rotation);
          break;
        case 'monochromatic':
          h = monochromaticHue(anchorHue);
          break;
      }

      // Rotate the whole scheme (keeps relative positions); analogous already
      // applied its own shift, so skip it there.
      if (theory !== 'analogous' && theory !== 'monochromatic') {
        h = normalizeHue(h + p.rotation);
      }

      const L = theory === 'monochromatic' || !preserveLightness
        ? Math.max(0, Math.min(100, lch.L + p.lDelta))
        : lch.L;
      const C = theory === 'monochromatic' ? lch.C * p.cFactor : lch.C;

      return gamutMapLchToLab({ L, C, h });
    });
    out.push(variant);
  }
  return out;
}
